import { useMemo } from 'react';
import { Box, Grid, IconButton, Paper, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { Controller, FormProvider } from 'react-hook-form';
import { Modal } from '../../../modals/Modal';
import { PasswordInput } from '../../Inputs/PasswordInput';
import { getPasswordRuleItems } from '../data/createGoogleUserPasswordSchema';
import { useCreateGoogleUserPassword } from '../hooks/useCreateGoogleUserPassword';
import type { CreateGoogleUserPasswordModalProps, PasswordRuleItem } from '../types';

const PasswordRule = ({ isValid, label }: PasswordRuleItem) => (
  <Box
    sx={{
      display: 'flex',
      marginLeft: '10px',
      color: isValid ? '#0DB66E' : '#F22E07',
      ...(label.includes('Uppercase') ? { marginTop: '-10px' } : {}),
    }}
  >
    {isValid ? (
      <DoneIcon style={{ fontSize: '18px' }} />
    ) : (
      <CloseIcon style={{ fontSize: '18px' }} />
    )}
    <Typography sx={{ fontSize: '12px', color: isValid ? '#0DB66E' : '#F22E07' }}>
      {label}
    </Typography>
  </Box>
);

export const CreateGoogleUserPasswordModal = ({
  isOpen,
  onClose,
}: CreateGoogleUserPasswordModalProps) => {
  const {
    formMethods,
    isLoading,
    username,
    handleSubmit,
    handleClose,
  } = useCreateGoogleUserPassword({ isOpen, onClose });

  const { control, watch } = formMethods;
  const password = watch('newPassword') || '';
  const passwordRules = useMemo(() => getPasswordRuleItems(password), [password]);
  const shouldShowPasswordRules = password.length > 0;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <Paper component="form" onSubmit={handleSubmit} noValidate>
          <input type="hidden" name="username" autoComplete="username" value={username} />
          <Modal.Header>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                padding: '5px 10px',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    color: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  Create Google User Password
                  <IconButton aria-label="close" className="btn" onClick={handleClose}>
                    <CloseIcon sx={{ color: '#fff' }} />
                  </IconButton>
                </Typography>
              </Box>
            </Box>
          </Modal.Header>
          <Modal.Body>
            <Box
              sx={{
                padding: { sm: '7px 20px', xs: '7px 8px' },
                borderRadius: '0 7px',
                transition: 'all 2s ease-in-out',
                width: { xs: '95%', sm: '450px' },
              }}
            >
              <Grid container rowGap={1} sx={{ marginTop: '10px' }}>
                <Grid item xs={12}>
                  <Controller
                    name="newPassword"
                    control={control}
                    render={({ field, fieldState }) => (
                      <PasswordInput
                        {...field}
                        label="New Password"
                        autoComplete="new-password"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', m: 0 }}>
                    {shouldShowPasswordRules &&
                      passwordRules.map((rule) => (
                        <PasswordRule key={rule.label} isValid={rule.isValid} label={rule.label} />
                      ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field, fieldState }) => (
                      <PasswordInput
                        {...field}
                        label="Confirm Password"
                        autoComplete="new-password"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Modal.Body>
          <Modal.Footer>
            <Box sx={{ display: 'flex', justifyContent: 'end', padding: '10px' }}>
              <LoadingButton
                loading={isLoading}
                loadingPosition="start"
                color="primary"
                startIcon={<SaveIcon />}
                variant="contained"
                type="submit"
                className="btn"
              >
                Create Password
              </LoadingButton>
            </Box>
          </Modal.Footer>
        </Paper>
      </FormProvider>
    </Modal>
  );
};

export default CreateGoogleUserPasswordModal;
